"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Colloca() {
  const {
    ref: contentRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [particles, setParticles] = useState<Array<{
    id: number;
    left: number;
    delay: number;
    duration: number;
    size: number;
  }>>([]);
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const generated = Array.from({
      length: 24
    }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 8,
      duration: 12 + Math.random() * 10,
      size: 1 + Math.random() * 3
    }));
    setParticles(generated);
  }, []);
  const stats = [{
    label: "SPECIALIZZAZIONE",
    value: "GINECOLOGIA"
  }, {
    label: "ESPERIENZA",
    value: "RIPRODUZIONE"
  }, {
    label: "CONSULENZA",
    value: "FORENSE"
  }, {
    label: "APPROCCIO",
    value: "PRECISIONE"
  }];
  return <section data-section-id="228" id="dott-mirko-colloca" ref={sectionRef} className="relative bg-background text-foreground overflow-hidden min-h-[90vh] flex items-end dark">
      {}
      <div className="absolute inset-0 overflow-hidden">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1724194/img/medical_clinical_precision.webp" alt="Dott. Mirko Colloca in un ambiente clinico professionale" loading="lazy" decoding="async" className="w-full h-full object-cover animate-[slowZoom_25s_ease-in-out_infinite_alternate]" />
        {}
        <div className="absolute inset-0" style={{
        background: "linear-gradient(90deg, hsla(215, 25%, 12%, 0.95) 0%, hsla(215, 25%, 12%, 0.85) 30%, hsla(215, 25%, 12%, 0.7) 60%, hsla(215, 25%, 12%, 0.5) 100%)"
      }} />
        <div className="absolute inset-0" style={{
        background: "linear-gradient(180deg, hsla(215, 25%, 12%, 0.6) 0%, transparent 30%, transparent 60%, hsla(215, 25%, 12%, 0.7) 100%)"
      }} />
      </div>

      {}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {particles.map((p, i) => <span key={p.id} data-index={i} className="absolute rounded-lg bg-primary/30" style={{
        left: `${p.left}%`,
        bottom: `-10px`,
        width: `${p.size}px`,
        height: `${p.size}px`,
        animation: `floatUp ${p.duration}s linear ${p.delay}s infinite`,
        filter: "blur(0.5px)"
      }} />)}
      </div>

      {}
      <div className="absolute top-8 right-4 md:top-12 md:right-12 lg:right-20 pointer-events-none select-none" aria-hidden="true">
        <span className="font-serif font-light leading-none text-foreground/[0.06]" style={{
        fontSize: "clamp(180px, 28vw, 380px)"
      }}>
          01
        </span>
      </div>

      {}
      <div className="hidden lg:block absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none z-10">
        <p className="font-mono text-[10px] tracking-[0.3em] text-foreground/50 uppercase whitespace-nowrap" style={{
        writingMode: "vertical-rl",
        transform: "rotate(180deg)"
      }}>
          MEDICO CHIRURGO &middot; SPECIALISTA &middot; GINECOLOGIA &middot; OSTETRICIA
        </p>
      </div>

      {}
      <div ref={contentRef} className={`relative z-10 w-full px-6 md:px-12 lg:px-20 xl:pl-[120px] xl:pr-20 pt-32 pb-16 md:pb-20 lg:pb-24 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {}
        <div className="flex items-center gap-3 mb-8 md:mb-10">
          <span className="block w-12 h-px bg-primary" />
          <p className="font-mono text-xs tracking-[0.25em] text-primary uppercase">
            DOTT. MIRKO COLLOCA &middot; ECCELLENZA MEDICA
          </p>
        </div>

        {}
        <h1 className="font-serif font-light text-foreground leading-[0.85] tracking-tight mb-10 md:mb-12" style={{
        fontSize: "clamp(60px, 14vw, 160px)"
      }}>
          <span className="block">CURA</span>
          <span className="block pl-[0.5em]">E</span>
          <span className="block italic text-primary">PRECISIONE.</span>
        </h1>

        {}
        <p className="font-default text-base md:text-lg leading-relaxed text-foreground/80 max-w-[560px] mb-10 md:mb-12">
          Medico Chirurgo specialista in Ginecologia e Ostetricia, esperto in Fisiopatologia della Riproduzione Umana e Ginecologia Forense. Dedizione assoluta alla salute femminile con rigore scientifico e approccio empatico.
        </p>

        {}
        <div className="mb-10 md:mb-12 max-w-3xl">
          <div className="flex flex-wrap items-stretch gap-y-4 border-t border-b border-border/40 py-5">
            {stats.map((stat, i) => <div key={stat.label} data-index={i} className={`flex-1 min-w-[140px] px-4 md:px-5 ${i !== 0 ? "md:border-l border-border/40" : ""}`}>
                <p className="font-mono text-[10px] tracking-[0.2em] text-foreground/50 uppercase mb-1.5">
                  {stat.label}
                </p>
                <p className="font-mono text-sm md:text-base font-medium text-foreground tracking-wider">
                  {stat.value}
                </p>
              </div>)}
          </div>
        </div>

        {}
        <div className="flex flex-wrap items-center gap-4">
          <Button asChild size="lg" className="h-14 px-8 bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs tracking-[0.2em] uppercase rounded-lg shadow-lg">
            <Link to="/ContactsPage">
              Prenota Visita
              <ArrowRight className="ml-1 size-4" />
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="h-14 px-8 bg-transparent border border-foreground/30 text-foreground hover:bg-foreground/5 hover:text-foreground font-mono text-xs tracking-[0.2em] uppercase rounded-lg">
            <Link to="/ProfilePage">
              <Stethoscope className="mr-1 size-4" />
              Il Profilo
            </Link>
          </Button>
        </div>
      </div>

      {}
      <style>{`
        @keyframes slowZoom {
          0% { transform: scale(1); }
          100% { transform: scale(1.12); }
        }
        @keyframes floatUp {
          0% {
            transform: translateY(0) translateX(0);
            opacity: 0;
          }
          15% {
            opacity: 0.6;
          }
          85% {
            opacity: 0.4;
          }
          100% {
            transform: translateY(-105vh) translateX(20px);
            opacity: 0;
          }
        }
      `}</style>
    </section>;
}